import { ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import { Location } from '../../models/Location';
import {
  AdjustLocationModal,
  CreateLocationModal,
  DeleteLocationModal,
  EditLocationModal,
  LockLocationModal,
  UnlockLocationModal,
  LocationLabelModal,
} from '../modals';

// 🎯 Extender la interfaz del ref para incluir método personalizado
interface ExtendedModalCenterRef extends ModalCenterRef {
  openWithLocation: (location: Location) => void;
}

interface LocationLabelModalRef extends ModalCenterRef {
  openWithLocation: (location: Location) => void;
  openWithLocations: (locations: Location[]) => void;
}

export interface LocationsModalsRef {
  openCreateModal: () => void;
  openEditModal: (location: Location) => void;
  openDeleteModal: (location: Location) => void;
  openLockModal: (location: Location) => void;
  openUnlockModal: (location: Location) => void;
  openAdjustModal: (location: Location) => void;
  openLabelModal: (location: Location) => void;
  openBulkLabelModal: (locations: Location[]) => void;
}

interface LocationsModalsProps {
  onRefetch?: () => void;
  onBulkLabelsGenerated?: () => void;
}

const LocationsModals = forwardRef<LocationsModalsRef, LocationsModalsProps>(
  ({ onRefetch, onBulkLabelsGenerated }, ref) => {
    const createModalRef = useRef<ModalCenterRef>(null);
    const editModalRef = useRef<ExtendedModalCenterRef>(null);
    const deleteModalRef = useRef<ExtendedModalCenterRef>(null);
    const lockModalRef = useRef<ExtendedModalCenterRef>(null);
    const unlockModalRef = useRef<ExtendedModalCenterRef>(null);
    const adjustModalRef = useRef<ExtendedModalCenterRef>(null);
    const labelModalRef = useRef<LocationLabelModalRef>(null);

    useImperativeHandle(ref, () => ({
      openCreateModal: () => {
        createModalRef.current?.openModal();
      },
      openEditModal: (location: Location) => {
        editModalRef.current?.openWithLocation(location);
      },
      openDeleteModal: (location: Location) => {
        deleteModalRef.current?.openWithLocation(location);
      },
      openLockModal: (location: Location) => {
        lockModalRef.current?.openWithLocation(location);
      },
      openUnlockModal: (location: Location) => {
        unlockModalRef.current?.openWithLocation(location);
      },
      openAdjustModal: (location: Location) => {
        adjustModalRef.current?.openWithLocation(location);
      },
      openLabelModal: (location: Location) => {
        labelModalRef.current?.openWithLocation(location);
      },
      openBulkLabelModal: (locations: Location[]) => {
        labelModalRef.current?.openWithLocations(locations);
      },
    }));

    return (
      <>
        {/* 🎯 Modales - Usan patrón de estado interno para ZERO re-renders */}
        <CreateLocationModal ref={createModalRef} />
        <EditLocationModal ref={editModalRef} />
        <DeleteLocationModal ref={deleteModalRef} />
        <LockLocationModal ref={lockModalRef} onSuccess={onRefetch} />
        <UnlockLocationModal ref={unlockModalRef} onSuccess={onRefetch} />
        <AdjustLocationModal ref={adjustModalRef} onSuccess={onRefetch} />
        <LocationLabelModal ref={labelModalRef} onBulkLabelsGenerated={onBulkLabelsGenerated} />
      </>
    );
  },
);

LocationsModals.displayName = 'LocationsModals';

export default LocationsModals;
